import { getStaffToken } from '../../shared/auth/AuthContext'
import type {
  AssignmentChange,
  AssignmentPreview,
  AssignmentsResponse,
  FutureBookings,
  CalendarSync,
  ClosureIn,
  ForcedResponse,
  HoursResponse,
  NotifyPreview,
  NotifyResult,
  OrganizationResponse,
  Grant,
  LocationsResponse,
  Person,
  SettingsPermissions,
  UpdateResult,
  Week,
  PortalAccessResponse,
  AccessLevel,
} from './types'

const API_BASE = import.meta.env.VITE_SETTINGS_API_BASE || 'https://ops-settings-api-1203871327.us-central1.run.app'

async function call<T>(path: string, method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE' = 'GET', body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${getStaffToken() ?? ''}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => null)
    throw new Error(typeof b?.detail === 'string' ? b.detail : `Request failed (${res.status})`)
  }
  return res.json()
}

export const fetchPermissions = () => call<SettingsPermissions>('/settings/permissions')
export const fetchLocations = () => call<LocationsResponse>('/settings/locations')

export const updateLocation = (slug: string, patch: Record<string, unknown>) =>
  call<UpdateResult>(`/settings/locations/${encodeURIComponent(slug)}`, 'PATCH', patch)

export const fetchOrganization = () => call<OrganizationResponse>('/settings/organization')

type Saved = { ok: boolean; calendar_sync?: CalendarSync }
export const addClosure = (c: ClosureIn) => call<Saved>('/settings/organization/closures', 'POST', c)
export const updateClosure = (id: string, c: ClosureIn) =>
  call<Saved>(`/settings/organization/closures/${encodeURIComponent(id)}`, 'PATCH', c)
export const deleteClosure = (id: string) =>
  call<Saved>(`/settings/organization/closures/${encodeURIComponent(id)}/delete`, 'POST')
export const syncCalendars = () => call<CalendarSync>('/settings/organization/calendar-sync', 'POST')

export const fetchNotifyPreview = (id: string) =>
  call<NotifyPreview>(`/settings/organization/closures/${encodeURIComponent(id)}/notify`)
export const sendNotifyTest = (id: string) =>
  call<{ ok: boolean; sent_to: string }>(`/settings/organization/closures/${encodeURIComponent(id)}/notify/test`, 'POST')
// One batch per call (bulk-send standard): start=true on the first press, then repeat while remaining > 0.
export const sendNotify = (id: string, opts: { start?: boolean; retrySince?: string } = {}) => {
  const q = new URLSearchParams()
  if (opts.start) q.set('start', 'true')
  if (opts.retrySince) q.set('retry_since', opts.retrySince)
  const qs = q.toString()
  return call<NotifyResult>(`/settings/organization/closures/${encodeURIComponent(id)}/notify${qs ? `?${qs}` : ''}`, 'POST')
}
export const addHoliday = (holiday_date: string, holiday_name: string) =>
  call<{ ok: boolean }>('/settings/organization/holidays', 'POST', { holiday_date, holiday_name })
export const deleteHoliday = (holiday_date: string) =>
  call<{ ok: boolean }>('/settings/organization/holidays/delete', 'POST', { holiday_date })


export const fetchGrants = () => call<{ grants: Grant[] }>('/settings/access').then((r) => r.grants)
export const fetchPeople = () => call<{ people: Person[] }>('/settings/people').then((r) => r.people)
export const grantAccess = (email: string, scope: string, location_slug: string | null) =>
  call<{ ok: boolean }>('/settings/access', 'POST', { email, scope, location_slug })
export const revokeAccess = (email: string, scope: string, location_slug: string | null) =>
  call<{ ok: boolean }>('/settings/access/revoke', 'POST', { email, scope, location_slug })

export const fetchLocationHours = (slug: string) =>
  call<HoursResponse>(`/settings/locations/${encodeURIComponent(slug)}/hours`)
export const saveLocationHours = (slug: string, kind: string, schedule_id: string, week: Week) =>
  call<{ ok: boolean }>(`/settings/locations/${encodeURIComponent(slug)}/hours/${kind}`, 'PUT', { schedule_id, week })
export const saveLocationDates = (
  slug: string,
  kind: string,
  schedule_id: string,
  date: string,
  end_date: string | null,
  intervals: { from: string; to: string }[],
) =>
  call<{ ok: boolean }>(`/settings/locations/${encodeURIComponent(slug)}/hours/${kind}/dates`, 'PUT', {
    schedule_id,
    date,
    end_date,
    intervals,
  })
export const deleteLocationDate = (slug: string, kind: string, schedule_id: string, date: string) =>
  call<{ ok: boolean }>(`/settings/locations/${encodeURIComponent(slug)}/hours/${kind}/dates/delete`, 'POST', {
    schedule_id,
    date,
  })
export const fetchForced = () => call<ForcedResponse>('/settings/organization/forced-interviewers')
export const saveForcedHours = (schedule_id: string, week: Week) =>
  call<{ ok: boolean }>('/settings/organization/forced-interviewers/hours', 'PUT', { schedule_id, week })
export const setForcedDay = (schedule_id: string, date: string, available: boolean) =>
  call<{ ok: boolean }>(`/settings/organization/forced-interviewers/${encodeURIComponent(schedule_id)}/day`, 'POST', { date, available })

export const fetchAssignments = (slug: string) =>
  call<AssignmentsResponse>(`/settings/locations/${encodeURIComponent(slug)}/assignments`)
type AssignmentBody = { role: string; email: string; effective_date: string; options: Record<string, boolean | string | null> }
export const previewAssignment = (slug: string, b: AssignmentBody) =>
  call<AssignmentPreview>(`/settings/locations/${encodeURIComponent(slug)}/assignments/preview`, 'POST', b)
export const createAssignment = (slug: string, b: AssignmentBody) =>
  call<{ ok: boolean; change_id: string; applies_now: boolean }>(`/settings/locations/${encodeURIComponent(slug)}/assignments`, 'POST', b)
export const advanceAssignment = (id: string) => call<AssignmentChange>(`/settings/assignments/${id}/advance`, 'POST')
export const retryAssignment = (id: string) => call<AssignmentChange>(`/settings/assignments/${id}/retry`, 'POST')
export const cancelAssignment = (id: string) => call<{ ok: boolean }>(`/settings/assignments/${id}/cancel`, 'POST')
export const markAssignmentStep = (id: string, key: string, done: boolean) =>
  call<{ ok: boolean }>(`/settings/assignments/${id}/manual/${key}?done=${done}`, 'POST')
export const fetchFutureBookings = (id: string) => call<FutureBookings>(`/settings/assignments/${id}/bookings`)
export const enableMoveBookings = (id: string) => call<AssignmentChange>(`/settings/assignments/${id}/move-bookings`, 'POST')

export const fetchPortalAccess = () => call<PortalAccessResponse>('/settings/portal-access')
export const setPortalAccess = (role: string, module: string, level: AccessLevel) =>
  call<{ ok: boolean; changed: boolean }>('/settings/portal-access', 'PUT', { role, module, level })

// ── Settings → Data (module data_admin) ─────────────────────────────────
export type DataGaps = { classes: number | null; categories: number | null; locations: number | null; names: number | null }
export type ClassRow = { class_name: string; centre: string | null; capacity: number | null; children: number; last_seen: string | null; in_brightwheel: boolean; program: string | null }
export type CategoryRow = { raw_category: string; charges: number; last_due: string | null; mapped_category: string | null; mapped_by: string | null }
export type LocationNameRow = { name: string; sources: string; rows: number; location_slug: string | null }
export type NameRow = { original_name: string; processing_status: string | null; notes: string | null; created_at: string | null }
export type ExportKind = 'students' | 'attendance' | 'billing' | 'rooms' | 'roster'

export const fetchDataGaps = () => call<DataGaps>('/settings/data/gaps')
export const fetchDataClasses = () => call<{ rows: ClassRow[]; programs: string[] }>('/settings/data/classes')
export const saveDataClass = (class_name: string, program: string | null) =>
  call<{ ok: boolean }>('/settings/data/classes', 'PUT', { class_name, program })
export const fetchDataCategories = () => call<{ rows: CategoryRow[]; categories: string[] }>('/settings/data/categories')
export const saveDataCategory = (raw_category: string, mapped_category: string | null) =>
  call<{ ok: boolean }>('/settings/data/categories', 'PUT', { raw_category, mapped_category })
export const fetchDataLocations = () =>
  call<{ rows: LocationNameRow[]; locations: { slug: string; name: string }[] }>('/settings/data/locations')
export const addLocationAlias = (name: string, location_slug: string) =>
  call<{ ok: boolean }>('/settings/data/locations/alias', 'POST', { name, location_slug })
export const fetchDataNames = () => call<{ children: NameRow[]; parents: NameRow[] }>('/settings/data/names')
export const saveDataName = (kind: 'children' | 'parents', original_name: string, first_name: string, last_name: string) =>
  call<{ ok: boolean }>('/settings/data/names', 'PUT', { kind, original_name, first_name, last_name })
export const startExport = (kind: ExportKind, body: Record<string, string> = {}) =>
  call<Record<string, unknown>>(`/settings/data/exports/${kind}`, 'POST', body)
export const exportStatus = (kind: ExportKind, runId: string) =>
  call<Record<string, unknown>>(`/settings/data/exports/${kind}/${encodeURIComponent(runId)}`)
export const setPortalRole = (role: string, levels: Record<string, AccessLevel>) =>
  call<{ ok: boolean; changed: string[] }>('/settings/portal-access/role', 'PUT', { role, levels })
// Roles and the people in them, managed on Settings → Access (Kate, 2026-10-22)
export const createPortalRole = (label: string, copy_from: string | null) =>
  call<{ ok: boolean; role: string; label: string }>('/settings/portal-access/roles', 'POST', { label, copy_from })
export const renamePortalRole = (role: string, label: string) =>
  call<{ ok: boolean }>(`/settings/portal-access/roles/${encodeURIComponent(role)}`, 'PATCH', { label })
export const deletePortalRole = (role: string) =>
  call<{ ok: boolean }>(`/settings/portal-access/roles/${encodeURIComponent(role)}`, 'DELETE')
export const addRoleMember = (role: string, email: string) =>
  call<{ ok: boolean; changed: boolean }>('/settings/portal-access/members', 'POST', { role, email })
export const removeRoleMember = (role: string, email: string) =>
  call<{ ok: boolean; changed: boolean }>('/settings/portal-access/members/remove', 'POST', { role, email })

// Settings → Data → Employee sync (doc 49)
export interface EmployeesSync {
  runs: { name: string; started: string | null; finished: string | null; state: 'running' | 'succeeded' | 'failed' }[]
  error: string | null
  synced_at: string | null
  last_log: Record<string, number>
  last_log_at: string | null
  dry_run: boolean
}
export const fetchEmployeesSync = () => call<EmployeesSync>('/settings/data/employees-sync')
export const runEmployeesSync = () => call<{ ok: boolean }>('/settings/data/employees-sync/run', 'POST')
